import { useGetIdentity, useTranslation } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import { FullScreenLoading } from "components/fullScreenLoading";
import { IUserData } from "interfaces";
import { get, isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";
import ApiService from "utills/ApiService";
import useWindowWidth from "utills/InitWidthDevice";
import { TemplateDataType } from "../../types";
import Input from "components/input";
import { isHaveActionPermission } from "utills/helpers";
import { PROVINCE } from "utills/constants";
import Button from "components/button";
import { ConfirmModal } from "components/confirmModal";
import { MailIcon, PencilIcon } from "@heroicons/react/outline";
import { CollapsibleSection } from "components/collapsibleSection";
import { Tag } from "components/tag";
import moment from "moment";

const EMAIL_ACTIONS = {
  registration: {
    endpoint: "participants/send-registration-email",
    successMessage: "registration-email-sent-successfully",
    confirmMessage: "confirm-send-registration-email",
  },
  ewaiver: {
    endpoint: "participants/send-ewaiver-link",
    successMessage: "ewaiver-email-sent-successfully",
    confirmMessage: "confirm-send-ewaiver-email",
  },
  qrcode: {
    endpoint: "participants/send-qr-code",
    successMessage: "qrcode-email-sent-successfully",
    confirmMessage: "confirm-send-qrcode-email",
  },
} as const;

type EmailType = keyof typeof EMAIL_ACTIONS;

interface ParticipantSendEmailConfig {
  participantId: string;
  is_show_send_ewaiver: boolean;
  is_show_send_qrcode: boolean;
}

const WPSAttendeesSportsDetail = () => {
  const params = useParams();
  const navigate = useNavigate();
  const windowWidth = useWindowWidth();
  const { data: user } = useGetIdentity<IUserData>();
  const { translate, getLocale } = useTranslation();
  const language = getLocale();

  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [participant, setParticipant] = useState<any>(null);
  const [permissions, setPermissions] = useState(null as any);
  const [sendingEmail, setSendingEmail] = useState<EmailType | null>(null);
  // Keep the selected message while the modal's leave transition runs.
  const [confirmEmailType, setConfirmEmailType] = useState<EmailType>("registration");
  const [isEmailConfirmOpen, setIsEmailConfirmOpen] = useState(false);
  const [sendEmailConfig, setSendEmailConfig] = useState<ParticipantSendEmailConfig | null>(null);

  const breadcrumbItems = [
    {
      label: translate("attendees"),
      href: "/attendees/sports",
    },
    {
      label: translate("sport-attendees-detail"),
      href: `/attendees/sports/${params?.id}/detail`,
    },
  ];

  const handleNavigateOrderDetail = (orderCode: string) => {
    navigate(`/orders/${orderCode}`);
  };

  const handleNavigateEdit = (id: string) => {
    navigate(`/attendees/sports/${id}/edit`);
  };

  const eventEndDate = participant?.participant?.end_date;
  const isEventEnded = Boolean(eventEndDate) && moment(eventEndDate).isValid() && moment().isSameOrAfter(eventEndDate);
  const currentEmailConfig = sendEmailConfig?.participantId === params?.id ? sendEmailConfig : null;
  const hasEwaiverSetup = !isEmpty(participant?.waiver_setup);
  const canSendEmail = (type: EmailType) => {
    if (type === "registration") return !isEventEnded;
    if (type === "ewaiver") return currentEmailConfig?.is_show_send_ewaiver === true && hasEwaiverSetup;
    return currentEmailConfig?.is_show_send_qrcode === true;
  };

  const handleSendEmail = async (type: EmailType) => {
    if (!user?.token || !params?.id || sendingEmail || !canSendEmail(type)) return;

    setSendingEmail(type);
    try {
      await ApiService.post(
        EMAIL_ACTIONS[type].endpoint,
        { participant_ids: [params.id] },
        user.token
      );
      toast.success(translate(EMAIL_ACTIONS[type].successMessage));
    } catch (error: any) {
      toast.error(error?.message ?? translate("send-email-failed"));
    } finally {
      setSendingEmail(null);
    }
  };

  const handleRequestSendEmail = (type: EmailType) => {
    if (sendingEmail || !canSendEmail(type)) return;

    setConfirmEmailType(type);
    setIsEmailConfirmOpen(true);
  };

  const handleGetCheckBoxValue = (value: string | boolean) => {
    if (value === "0") return false;
    if (value === "1") return true;
    if (value === true) return true;
    if (value === false) return false;

    return false;
  };

  useEffect(() => {
    if (windowWidth && windowWidth < 1024) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [windowWidth]);

  useEffect(() => {
    const token = user?.token;
    const id = params?.id;
    let cancelled = false;
    setParticipant(null);
    setSendEmailConfig(null);
    setIsEmailConfirmOpen(false);
    if (!token || !id) {
      setLoading(false);
      return;
    }

    const loadParticipant = async () => {
      setLoading(true);
      try {
        const data = await ApiService.get(`participants/${id}`, token);
        if (cancelled) return;
        setParticipant(data);
        setSendEmailConfig({
          participantId: id,
          is_show_send_ewaiver: (data?.is_show_send_ewaiver ?? data?.participant?.is_show_send_ewaiver) === true,
          is_show_send_qrcode: (data?.is_show_send_qrcode ?? data?.participant?.is_show_send_qrcode) === true,
        });
      } catch (error: any) {
        if (!cancelled) toast.error(error?.message ?? translate("participant-detail-load-failed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadParticipant();
    return () => { cancelled = true; };
  }, [user?.token, params?.id, translate]);

  useEffect(() => {
    const user = localStorage.getItem("USER");

    if (user) {
      const userData = JSON.parse(user);

      if (userData?.type === "wps") {
        const roles = [] as any;

        userData.permissions.forEach((item: any) => {
          const obj = {
            id: item.resource !== "" ? item.resource : item.app,
            permissions: item.action,
            parrent: item.app,
          };

          roles.push(obj);

          setPermissions(roles);
        });
      }
    }
  }, [windowWidth]);

  return (
    <div className="w-full">
      {loading && <FullScreenLoading />}

      <Breadcrumbs
        type="wps"
        breadcrumbItems={breadcrumbItems}
        disableBorder={true}
        additionalLabel={(
          <div className="flex flex-wrap justify-end gap-2">
            {params?.id &&
              isHaveActionPermission(
                "sports",
                "attendees",
                "edit",
                permissions
              ) &&
              (() => {
                const id = params?.id;

                // return (
                //   <button
                //     type="button"
                //     onClick={() => handleNavigateEdit(id)}
                //     className="h-[48px] px-[12px] flex items-center justify-center gap-1
                //   border-[1px] rounded-[4px] border-[#011BB6] cursor-pointer"
                //   >
                //     <img
                //       src="/images/icons/icon-edit-detail.svg"
                //       className="mr-[4px]"
                //       alt="icon-edit"
                //     />
                //     <span className="text-[14px] font-[500] text-[#011BB6]">
                //       {translate("edit")}
                //     </span>
                //   </button>
                // );

                return (
                  <>
                    <Button
                      className={`w-11 h-11 sm:w-auto ${
                        isEventEnded
                          ? "bg-gray-400 hover:bg-gray-400 active:bg-gray-400 focus-visible:outline-gray-400"
                          : "bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 focus-visible:outline-emerald-600"
                      }`}
                      onClick={() => handleRequestSendEmail("registration")}
                      disabled={isEventEnded || sendingEmail !== null}
                      loading={sendingEmail === "registration"}
                      title={isEventEnded ? translate("event-has-ended") : translate("resend-registration-email")}
                    >
                      <MailIcon className="w-4 h-4 text-white" />{" "}
                      <span className="hidden sm:block sm:ml-2">
                        {isEventEnded ? translate("event-has-ended") : translate("resend-registration-email")}
                      </span>
                    </Button>
                    {canSendEmail("ewaiver") ? (
                      <Button
                        className="w-11 h-11 sm:w-auto bg-orange-500 hover:bg-orange-400 active:bg-orange-600 focus-visible:outline-orange-500"
                        onClick={() => handleRequestSendEmail("ewaiver")}
                        disabled={sendingEmail !== null}
                        loading={sendingEmail === "ewaiver"}
                        title={translate("send-ewaiver-email")}
                      >
                        <MailIcon className="w-4 h-4 text-white" />{" "}
                        <span className="hidden sm:block sm:ml-2">{translate("send-ewaiver-email")}</span>
                      </Button>
                    ) : null}
                    {currentEmailConfig?.is_show_send_qrcode === true ? (
                      <Button
                        className="w-11 h-11 sm:w-auto"
                        onClick={() => handleRequestSendEmail("qrcode")}
                        disabled={sendingEmail !== null}
                        loading={sendingEmail === "qrcode"}
                        title={translate("send-qrcode-email")}
                      >
                        <MailIcon className="w-4 h-4 text-white" />{" "}
                        <span className="hidden sm:block sm:ml-2">{translate("send-qrcode-email")}</span>
                      </Button>
                    ) : null}
                    <Button
                      className="w-11 h-11 sm:w-auto"
                      onClick={() => handleNavigateEdit(id)}
                    >
                      <PencilIcon className="w-4 h-4 text-white" />{" "}
                      <span className="hidden sm:block sm:ml-2">{translate("edit")}</span>
                    </Button>
                  </>
                );
              })()}
          </div>
        )}
      />

      <ConfirmModal
        open={isEmailConfirmOpen}
        onClose={() => setIsEmailConfirmOpen(false)}
        onConfirm={() => {
          if (isEmailConfirmOpen) {
            handleSendEmail(confirmEmailType);
          }
        }}
        title={translate("confirm-send-email")}
        content={translate(EMAIL_ACTIONS[confirmEmailType].confirmMessage)}
      />

      <div className="w-full">
        {/* Main Content */}
        {!loading && participant && (
          <div className="mb-[16px] p-4 flex flex-col gap-5">
            {/* General information */}
            <CollapsibleSection
              defaultOpen={true}
              label={translate("general-information")}
              additionalLabel={(
                <Tag text={participant?.participant.source ?? ""} customClassName="capitalize" type="blue" />
              )}
            >
              <div
                  className={`w-full p-6 grid grid-cols-1 lg:grid-cols-2 gap-4`}
                >
                <div className="w-full flex">
                  <span className="w-[200px] max-w-[200px] text-[14px] font-[500]">
                    {translate("status")}
                  </span>

                  {participant?.participant?.status === 1 ? (
                    <span className="w-[118px] py-1 rounded-[12px] font-medium flex justify-center bg-[#DCFCE7] text-[#1D813F]">
                      {translate("active")}
                    </span>
                  ) : (
                    <span className="w-[118px] py-1 rounded-[12px] font-medium flex justify-center bg-[#FFCFDD] text-[#9D0520]">
                      {translate("inactive")}
                    </span>
                  )}
                </div>

                <div className="w-full flex">
                  <span className="w-[200px] max-w-[200px] text-[14px] font-[500]">
                    {translate("category")}
                  </span>
                  <span className="text-[14px] font-[400]">
                    {participant?.participant?.category_name_en}
                  </span>
                </div>

                <div className="w-full flex">
                  <span className="w-[200px] max-w-[200px] text-[14px] font-[500]">
                    {translate("event-name")}
                  </span>
                  <span className="text-[14px] font-[400]">
                    {get(
                      participant?.participant,
                      `event_name_${language}`,
                      ""
                    )}
                  </span>
                </div>

                <div className="w-full flex">
                  <span className="w-[200px] max-w-[200px] text-[14px] font-[500]">
                    {translate("is-tranferred")}
                  </span>

                  {participant?.participant?.is_transferred === "1" ? (
                    <span className="text-[14px] font-[400]">
                      {translate("yes")}
                    </span>
                  ) : (
                    <span className="text-[14px] font-[400]">
                      {translate("not")}
                    </span>
                  )}
                </div>

                <div className="w-full flex">
                  <span className="w-[200px] max-w-[200px] text-[14px] font-[500]">
                    {translate("ticket-name")}
                  </span>
                  <span className="text-[14px] font-[400]">
                    {get(
                      participant?.participant,
                      `product_name_${language}`,
                      ""
                    )}
                  </span>
                </div>

                <div className="w-full flex">
                  <span className="mr-[50px] w-[150px] max-w-[150px] text-[14px] font-[500]">
                    {translate("is-change-distance")}
                  </span>

                  {participant?.participant?.is_change_distance ? (
                    <span className="text-[14px] font-[400]">
                      {translate("yes")}
                    </span>
                  ) : (
                    <span className="text-[14px] font-[400]">
                      {translate("not")}
                    </span>
                  )}
                </div>

                <div className="w-full flex">
                  <span className="w-[200px] max-w-[200px] text-[14px] font-[500]">
                    {translate("time-to-change-distance")}
                  </span>
                  <span className="text-[14px] font-[400]">
                    {participant?.participant?.time_to_change_distance}
                  </span>
                </div>

                <div className="w-full flex">
                  <span className="w-[200px] max-w-[200px] text-[14px] font-[500]">
                    {translate("order-code")}
                  </span>

                  {/* {participant?.participant?.order_code && (
                    <span
                      className="text-[#011BB6] text-[14px] font-[400] hover:cursor-pointer"
                      onClick={() => {
                        handleNavigateOrderDetail(
                          participant?.participant?.order_code
                        );
                      }}
                    >
                      {participant?.participant?.order_code}
                    </span>
                  )} */}

                  {participant?.participant?.order_code && (
                    <Link
                      to={`/orders/${participant?.participant?.order_code}`}
                      className="w-fit font-medium text-brand-500 hover:underline"
                    >
                      {participant?.participant?.order_code}
                    </Link>
                  )}
                </div>
              </div>
            </CollapsibleSection>

            {/* Registration information */}
            <CollapsibleSection
              defaultOpen={true}
              label={translate("registration-information")}
            >
              <div className="p-6">
                {Object.entries(
                  (participant?.template_data as TemplateDataType) || {}
                ).map(([key, value]: [string, TemplateDataType], index, arr) => {
                  const isLast = index === arr.length - 1;

                  return (
                    <div key={key} className="mb-[30px] flex flex-col gap-5">
                      {/* <div className="text-lg font-semibold mb-2">
                        {get(value, `label_${language}`, "")}
                      </div> */}

                      <h3 className="my-0 text-[0.95rem] font-bold text-gray-600">
                        {get(value, `label_${language}`, "")}
                      </h3>

                      {/* Form field render */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {value?.fields?.map((field) => {
                          let fieldValue =
                            participant?.registration[field?.key] || "";

                          // Format province_living
                          if (field?.key === "province_living") {
                            const province = field?.option[fieldValue];

                            fieldValue = get(
                              province,
                              `label_${language}`,
                              fieldValue
                            );
                          }

                          // Format club
                          if (field?.key === "club") {
                            const club = field?.option[fieldValue];

                            fieldValue = get(
                              club,
                              `label_${language}`,
                              fieldValue
                            );
                          }

                          // Format nationality
                          if (field?.key === "nationality") {
                            const country = field?.option[fieldValue];

                            fieldValue = get(
                              country,
                              `label_${language}`,
                              fieldValue
                            );
                          }

                          // Checkbox type render
                          if (field?.type === "checkbox") {
                            return (
                              <div
                                key={field?.key}
                                className="flex items-start"
                              >
                                <div className="w-[200px] max-w-[200px] text-[14px] font-[500]">
                                  {get(field, `label_${language}`, "")}
                                </div>

                                {/* <input
                                  disabled
                                  className="focus:outline-none focus:ring-0"
                                  type="checkbox"
                                  checked={handleGetCheckBoxValue(fieldValue)}
                                  onChange={() => {}}
                                /> */}

                                <Input
                                  disabled
                                  className="px-2 py-2"
                                  type="checkbox"
                                  checked={handleGetCheckBoxValue(fieldValue)}
                                  onChange={() => {}}
                                />
                              </div>
                            );
                          }

                          return (
                            <div
                              key={field?.key}
                              className="flex items-start"
                            >
                              <div className="w-[200px] max-w-[200px] text-[14px] font-[500]">
                                {get(field, `label_${language}`, "")}
                              </div>

                              {field?.key?.indexOf("phone") === -1 ? (
                                <span className="text-[14px] font-[400]">
                                  {fieldValue}
                                </span>
                              ) : (
                                <span className="text-[14px] font-[400]">
                                  +
                                  {get(
                                    participant?.registration,
                                    `${field?.key}_area_code`,
                                    ""
                                  )}{" "}
                                  {fieldValue}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {!isLast && <div className="w-full h-[1px] bg-gray-200"></div>}
                    </div>
                  );
                })}
              </div>
            </CollapsibleSection>
          </div>
        )}
      </div>
    </div>
  );
};

export default WPSAttendeesSportsDetail;
